/* Read-only trade detail modal — click any row in the Positions table to open.
   Bundles the trade row + all trade events + the parsed signal it originated
   from so the user can audit the full lifecycle without leaving the page. */
import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { trades as tradesApi } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Badge, cn } from "@/components/atoms";
import { Eye } from "@phosphor-icons/react";

function _roundNum(n) {
  // Prices/quantities can carry full float precision from exact exchange
  // fill data (e.g. reconciled trades) - round to 6 decimals and drop
  // trailing zeros rather than rendering the raw value verbatim.
  return parseFloat(n.toFixed(6));
}

function _fmt(v) {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "number") return _roundNum(v).toString();
  if (typeof v === "boolean") return v ? "yes" : "no";
  if (Array.isArray(v)) {
    return v.map((x) => (typeof x === "number" ? _roundNum(x) : x)).join(", ") || "—";
  }
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
}

function _pnlTone(v) {
  if (typeof v !== "number") return "";
  if (v > 0) return "text-emerald-600";
  if (v < 0) return "text-red-600";
  return "";
}

export default function TradeDetailDialog({ tradeId, trigger }) {
  const [open, setOpen] = useState(false);
  const q = useQuery({
    queryKey: ["trade-detail", tradeId],
    queryFn: async () => (await tradesApi.detail(tradeId)).data,
    enabled: open,      // only fetch when modal opens
    staleTime: 5_000,
  });

  const trade = q.data?.trade;
  const events = q.data?.events || [];
  const signal = q.data?.signal;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {trigger || (
        <Button
          onClick={() => setOpen(true)}
          variant="ghost" size="sm"
          className="h-8 px-2 text-slate-600 hover:text-slate-900"
          data-testid={`trade-detail-btn-${tradeId}`}
        >
          <Eye size={16} />
        </Button>
      )}
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto"
                     data-testid="trade-detail-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-3">
            {trade?.symbol ?? "Trade"}
            {trade?.side && (
              <Badge variant={trade.side === "long" ? "profit" : "loss"}>
                {trade.side.toUpperCase()}
              </Badge>
            )}
            {trade?.status && <Badge variant="info">{trade.status}</Badge>}
          </DialogTitle>
          <DialogDescription>
            {trade?.id ? <span className="mono text-[11px]">{trade.id}</span> : null}
          </DialogDescription>
        </DialogHeader>

        {q.isLoading && <p className="text-sm text-slate-500 py-6 text-center">Loading…</p>}
        {q.isError && <p className="text-sm text-red-600 py-6 text-center">Failed to load trade.</p>}
        {trade && (
          <div className="space-y-4">
            {/* --- Metrics grid --- */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <_Metric label="Entry" value={_fmt(trade.entry_price)} mono />
              <_Metric label="Close" value={_fmt(trade.close_price)} mono />
              <_Metric label="Qty" value={_fmt(trade.quantity)} mono />
              <_Metric label="Leverage" value={trade.leverage ? `${trade.leverage}x` : "—"} mono />
              <_Metric label="Stop-loss" value={_fmt(trade.stop_loss)} mono tone="text-red-600" />
              <_Metric label="Take-profits" value={_fmt(trade.take_profits)} mono tone="text-emerald-600" />
              <_Metric label="Realized PnL" value={_fmt(trade.realized_pnl)} mono tone={_pnlTone(trade.realized_pnl)} />
              <_Metric label="Unrealized PnL" value={_fmt(trade.unrealized_pnl)} mono tone={_pnlTone(trade.unrealized_pnl)} />
              <_Metric label="SL source" value={_fmt(trade.sl_source)} />
              <_Metric label="Close reason" value={_fmt(trade.close_reason)} />
              <_Metric label="Opened" value={_fmt(trade.created_at)} mono />
              <_Metric label="Closed" value={_fmt(trade.closed_at)} mono />
            </div>

            {/* --- Originating signal --- */}
            {signal && (
              <div>
                <p className="overline mb-2">Originating signal</p>
                <pre className="text-[11px] mono bg-slate-50 border border-slate-200 rounded-md p-3 overflow-x-auto max-h-40"
                     data-testid="trade-detail-signal">
{JSON.stringify(signal, null, 2)}
                </pre>
              </div>
            )}

            {/* --- Event timeline --- */}
            <div>
              <p className="overline mb-2">Event timeline ({events.length})</p>
              {events.length === 0 && (
                <p className="text-xs text-slate-500">No events yet.</p>
              )}
              <ul className="space-y-1.5" data-testid="trade-detail-events">
                {events.map((ev) => (
                  <li key={ev.id} className="flex items-start gap-3 text-xs">
                    <span className="mono text-slate-500 shrink-0 whitespace-nowrap">
                      {String(ev.created_at || "").slice(11, 19)}
                    </span>
                    <span className={cn(
                      "shrink-0 font-medium",
                      ev.event_type?.includes("error") && "text-red-600",
                      ev.event_type?.includes("close") && "text-slate-800",
                      ev.event_type?.includes("sl_moved") && "text-emerald-700",
                    )}>
                      {ev.event_type}
                    </span>
                    <span className="text-slate-600 min-w-0 break-words">{ev.detail || "—"}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function _Metric({ label, value, mono, tone }) {
  return (
    <div className="rounded-md border border-slate-200 bg-white p-2">
      <p className="text-[10px] text-slate-500 uppercase tracking-wider">{label}</p>
      <p className={cn("text-sm mt-0.5 truncate", mono && "mono", tone)}>{value}</p>
    </div>
  );
}
